import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ProjectIdContext, ProjectDetailsContext, ProjectStateLoadingContext } from './Context';
import { useRegisterProjectChat } from './hooks/useRegisterChat';

type Props = {
  children: React.ReactNode;
};

export const ProjectContainer = (props: Props) => {
  const params = useParams<{ projectId: string }>();
  const rawParam = params.projectId ?? '';

  const isProjectParentSlug = rawParam.startsWith('P');
  const projectParentId = isProjectParentSlug ? Number(rawParam.substring(1)) : null;
  const [resolvedProjectId, setResolvedProjectId] = useState<number | null>(
    isProjectParentSlug ? null : Number(rawParam)
  );

  const apiClient = useChatApiClient();

  const parentQuery = useQuery({
    queryKey: ['get:projects/parent', projectParentId],
    queryFn: async () => {
      return apiClient.projects.getProjectParent({ projectParentId: projectParentId as number });
    },
    enabled: isProjectParentSlug && projectParentId !== null,
  });

  useEffect(() => {
    if (parentQuery.data?.projectParent?.latestChild) {
      setResolvedProjectId(parentQuery.data.projectParent.latestChild);
    }
  }, [parentQuery.data]);

  useEffect(() => {
    if (!isProjectParentSlug) {
      setResolvedProjectId(Number(rawParam));
    }
  }, [rawParam, isProjectParentSlug]);

  const query = useQuery({
    queryKey: [`get:projects`, resolvedProjectId],
    queryFn: async () => {
      return apiClient.projects.fetchProject({
        projectId: resolvedProjectId as number,
      });
    },
    enabled: !!resolvedProjectId,
  });

  useRegisterProjectChat({
    enabled: !!query.data,
    project: query.data
      ? {
          id: query.data.project.id,
          name: query.data.project.name,
          projectType: query.data.project.projectTypeId,
        }
      : null,
  });

  const isLoading = (isProjectParentSlug && !resolvedProjectId) || !query.data || query.isLoading;

  return (
    <ProjectIdContext.Provider value={resolvedProjectId}>
      <ProjectStateLoadingContext.Provider value={isLoading}>
        <ProjectDetailsContext.Provider value={{ query }}>{props.children}</ProjectDetailsContext.Provider>
      </ProjectStateLoadingContext.Provider>
    </ProjectIdContext.Provider>
  );
};

ProjectContainer.displayName = 'Project.Container';
